import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import ConfirmDialog from './ConfirmDialog'
import Modal from './Modal'

interface User {
  id: number
  name: string
}

interface VoteBreakdown {
  yes_count: number
  maybe_count: number
  no_count: number
  total_votes: number
  score: number
}

interface ProposedDate {
  id: number
  proposed_datetime: string
  location: string | null
  score: number
  vote_breakdown: VoteBreakdown
  current_user_vote: string | null
}

interface MeetingProposal {
  id: number
  book_pick_id: number
  created_by: User
  status: string
  finalized_at: string | null
  created_at: string
  proposed_dates: ProposedDate[]
}

interface MeetingVotingProps {
  bookPickId: number
  currentUserId: number
  isBookPicker: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function MeetingVoting({ bookPickId, currentUserId, isBookPicker, onClose, onSuccess }: MeetingVotingProps) {
  const [proposal, setProposal] = useState<MeetingProposal | null>(null)
  const [loading, setLoading] = useState(true)
  const [voting, setVoting] = useState(false)
  const [error, setError] = useState('')
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false)
  const [selectedDateId, setSelectedDateId] = useState<number | null>(null)

  useEffect(() => {
    fetchProposal()
  }, [bookPickId])

  const fetchProposal = async () => {
    try {
      const response = await fetch(`/api/v1/book_picks/${bookPickId}/meeting_proposals`)
      if (response.ok) {
        const data = await response.json()
        // Get the most recent open proposal
        const openProposal = data.proposals.find((p: MeetingProposal) => p.status === 'open')
        setProposal(openProposal || null)
      } else {
        setError('Failed to load proposal')
      }
    } catch (err) {
      setError('Failed to load proposal')
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (proposedDateId: number, vote: string) => {
    setVoting(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/proposed_dates/${proposedDateId}/vote/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_vote: { vote }
        })
      })

      if (response.ok) {
        await fetchProposal() // Refresh to show updated votes
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to vote')
      }
    } catch (err) {
      setError('Failed to vote')
    } finally {
      setVoting(false)
    }
  }

  const handleFinalize = (proposedDateId: number) => {
    setSelectedDateId(proposedDateId)
    setShowFinalizeConfirm(true)
  }

  const handleFinalizeConfirmed = async () => {
    if (!selectedDateId) return

    setShowFinalizeConfirm(false)
    setVoting(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/book_picks/${bookPickId}/meeting_proposals/${proposal!.id}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposed_date_id: selectedDateId })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to finalize')
      }
    } catch (err) {
      setError('Failed to finalize')
    } finally {
      setVoting(false)
      setSelectedDateId(null)
    }
  }

  const formatDateTime = (datetime: string) => {
    const date = new Date(datetime)
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true}>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </Modal>
    )
  }

  if (!proposal) {
    return (
      <Modal isOpen={true} onClose={onClose} size="md" showCloseButton={false}>
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Proposal</h3>
          <p className="text-gray-600 mb-6">There are no meeting dates to vote on yet.</p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all"
          >
            Close
          </button>
        </div>
      </Modal>
    )
  }

  // Sort dates by score (highest first)
  const sortedDates = [...proposal.proposed_dates].sort((a, b) => b.score - a.score)

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true} className="overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Vote on Meeting Dates</h2>
              <p className="text-gray-600">Proposed by {proposal.created_by.name}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {sortedDates.map((proposedDate, index) => {
              const { date, time } = formatDateTime(proposedDate.proposed_datetime)
              const userVote = proposedDate.current_user_vote
              const isTopChoice = index === 0 && proposedDate.score > 0

              return (
                <div
                  key={proposedDate.id}
                  className={`p-5 rounded-xl border-2 transition-all ${
                    isTopChoice
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Date Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">{date}</h3>
                        {isTopChoice && (
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-semibold rounded">
                            WINNING
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">{time}</p>
                      {proposedDate.location && (
                        <p className="text-sm text-gray-500 mt-1">📍 {proposedDate.location}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-600">{proposedDate.score}</div>
                      <div className="text-xs text-gray-500">score</div>
                    </div>
                  </div>

                  {/* Vote Breakdown */}
                  <div className="flex gap-2 text-sm mb-4">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                      ✓ {proposedDate.vote_breakdown.yes_count} yes
                    </span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                      ? {proposedDate.vote_breakdown.maybe_count} maybe
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                      ✗ {proposedDate.vote_breakdown.no_count} no
                    </span>
                  </div>

                  {/* Voting Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleVote(proposedDate.id, 'yes')}
                      disabled={voting}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 ${
                        userVote === 'yes'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
                      }`}
                    >
                      ✓ Yes
                    </button>
                    <button
                      onClick={() => handleVote(proposedDate.id, 'maybe')}
                      disabled={voting}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 ${
                        userVote === 'maybe'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-yellow-50 hover:text-yellow-700'
                      }`}
                    >
                      ? Maybe
                    </button>
                    <button
                      onClick={() => handleVote(proposedDate.id, 'no')}
                      disabled={voting}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 ${
                        userVote === 'no'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
                      }`}
                    >
                      ✗ No
                    </button>
                  </div>

                  {/* Finalize Button (only for book picker) */}
                  {isBookPicker && (
                    <button
                      onClick={() => handleFinalize(proposedDate.id)}
                      disabled={voting}
                      className="w-full mt-3 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 shadow-lg shadow-indigo-500/30"
                    >
                      Select This Date
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </Modal>

      {showFinalizeConfirm && (
        <ConfirmDialog
          message="Are you sure you want to finalize this date? This will schedule the meeting."
          onConfirm={handleFinalizeConfirmed}
          onCancel={() => {
            setShowFinalizeConfirm(false)
            setSelectedDateId(null)
          }}
          confirmText="Finalize"
          confirmButtonClass="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
        />
      )}
    </>
  )
}
